import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminUser, PORTAL_ROLES, ROLES } from "@/lib/roles";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Compass, ListChecks, MessagesSquare, Users, ClipboardCheck, Home } from "lucide-react";

const GUIDES = {
  administrador: {
    label: "Administrador",
    intro: "Desde aquí gestionas tus proyectos de principio a fin. Esto es lo que puedes hacer:",
    steps: [
      { icon: Compass, title: "Crea tu proyecto", text: "Añade nombre, municipio, presupuesto estimado y una descripción de la vivienda." },
      { icon: Users, title: "Invita a tu equipo", text: "Añade clientes y colaboradores por email. Recibirán una invitación para entrar." },
      { icon: ListChecks, title: "Sigue la hoja de ruta", text: "9 fases claras, del terreno a la entrada a vivir, con tareas y progreso visible." },
      { icon: MessagesSquare, title: "Pregunta a Mori", text: "Tu asistente resuelve dudas del proceso de autopromoción en lenguaje claro." },
    ],
  },
  cliente: {
    label: "Cliente",
    intro: "Este es tu espacio para seguir cómo avanza tu vivienda con total tranquilidad:",
    steps: [
      { icon: Home, title: "Sigue el avance", text: "Ve el progreso de tu casa fase a fase, con un porcentaje claro de cada etapa." },
      { icon: ClipboardCheck, title: "Consulta las tareas", text: "Revisa qué se está haciendo en cada fase del proyecto." },
      { icon: MessagesSquare, title: "Pregunta a Mori", text: "Resuelve tus dudas del proceso siempre que lo necesites, en lenguaje sencillo." },
    ],
  },
  colaborador: {
    label: "Colaborador",
    intro: "Desde tu portal participas en el día a día del proyecto:",
    steps: [
      { icon: ClipboardCheck, title: "Actualiza las tareas", text: "Marca tareas como completadas y añade nuevas dentro de cada fase." },
      { icon: ListChecks, title: "Sigue la hoja de ruta", text: "Consulta las 9 fases del proyecto y el progreso global." },
      { icon: MessagesSquare, title: "Pregunta a Mori", text: "Un asistente experto en el proceso de autopromoción en España." },
    ],
  },
};

function guideKeyFor(user) {
  if (isAdminUser(user)) return "administrador";
  for (const role of PORTAL_ROLES) {
    if (user.memberships?.some((m) => m.role === role)) return role;
  }
  return null;
}

export default function WelcomeGuide() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const dismissed = useRef(false);

  useEffect(() => {
    if (user && user.has_seen_welcome === false && !dismissed.current) {
      setOpen(true);
    }
  }, [user]);

  if (!user) return null;
  const key = guideKeyFor(user);
  if (!key) return null;
  const guide = GUIDES[key];

  const dismiss = async () => {
    dismissed.current = true;
    setOpen(false);
    setUser((u) => (u ? { ...u, has_seen_welcome: true } : u));
    try {
      await api.post("/auth/welcome-seen");
    } catch {
      /* ignore */
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="welcome-guide">
        <div className="-mx-6 -mt-6 mb-1 h-16 bg-[#C75B28] seigaiha-light rounded-t-lg flex items-end px-6 pb-2.5">
          <span className="text-[#F8F5EE]/90 text-[10px] uppercase tracking-[0.2em] font-mono">Bienvenido a mori</span>
        </div>
        <DialogHeader>
          <div className="flex items-center gap-2.5 mb-1">
            <img src="/mori-logo.jpg" alt="Mori" className="h-9 w-9 rounded-lg object-cover" />
            <span className="caption">{guide.label}</span>
          </div>
          <DialogTitle className="text-2xl">
            Te damos la bienvenida{user.name ? `, ${user.name.split(" ")[0]}` : ""}
          </DialogTitle>
          <DialogDescription>{guide.intro}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {guide.steps.map((s, i) => (
            <div key={s.title} className="flex gap-3.5 items-start" data-testid={`welcome-step-${i}`}>
              <div className="h-9 w-9 shrink-0 rounded-lg bg-[#C75B28]/10 text-[#C75B28] flex items-center justify-center">
                <s.icon className="h-4.5 w-4.5" />
              </div>
              <div>
                <div className="font-semibold text-sm">{s.title}</div>
                <div className="text-sm text-muted-foreground leading-relaxed">{s.text}</div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button onClick={dismiss} className="rounded-full w-full sm:w-auto !bg-[#C75B28] hover:!bg-[#A5461E] !text-[#F8F5EE]" data-testid="welcome-guide-dismiss">
            Empezar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
